import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import { Avatar, Box, Button, Chip, Drawer, IconButton, Paper, Snackbar, Stack, TextField, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import brandLockup from '../../../asset/logo/lockup-horizontal-color.svg';
import { BuilderRefreshButton, BuilderStageTabs, DrawerIconButton } from '../../../component/VerbaScholaLessonBuilderEditors';
import { BORDER, INTER, NAVY, PAGE_BACKGROUND, POPPINS, SLATE } from '../../../style/verbaScholaStyles';
import verbaScholaTheme from '../../../style/verbaScholaTheme';
import LessonDescriptionPdfEditor from '../component/LessonDescriptionPdfEditor';

const COPILOT_WIDTH = 360;

/** The live page's Lesson copilot as it opens on a new lesson's description. */
function CopilotPanel({ headerAction, onMockAction }: { headerAction: React.ReactNode; onMockAction: (message: string) => void }) {
    const [text, setText] = React.useState('');
    return (
        <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }} aria-label="Lesson copilot">
            <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography component="h2" variant="h6" sx={{ flex: 1 }}>Lesson copilot</Typography>
                    {headerAction}
                </Stack>
                <Typography variant="body2">Private to you. Ask about the saved lesson or select a card to request its revision. The first plan saves automatically when no earlier draft or plan exists. Later changes are reviewed on the page before they are saved.</Typography>
                <Box component="details"><Box component="summary" sx={{ cursor: 'pointer' }}>Choose lesson content</Box>
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                        <Chip size="small" label="Lesson description" variant="outlined" sx={{ minHeight: 44 }} onClick={() => undefined} />
                    </Stack>
                </Box>
                <TextField label="Message to copilot" multiline minRows={3} value={text} onChange={event => setText(event.target.value)} inputProps={{ maxLength: 4000 }} />
                <Button variant="contained" disabled={!text.trim()} onClick={() => { setText(''); onMockAction('Mockup: copilot messages are not sent.'); }}>Send message</Button>
            </Stack>
        </Paper>
    );
}

/**
 * Version B of the description step: the live Create lesson page (builder header, description form,
 * Lesson copilot) with textbook PDF upload. A static copy; compare with the live page when it changes.
 */
export default function LessonDescriptionPdfPage() {
    const history = useHistory();
    const { classId, lessonId } = useParams() as { classId: string; lessonId: string };
    const base = `/mockup/teacher/class/${classId}/lesson/${lessonId}`;
    const compactCopilot = useMediaQuery(verbaScholaTheme.breakpoints.down('lg'));
    const [copilotOpen, setCopilotOpen] = React.useState(false);
    const [copilotFolded, setCopilotFolded] = React.useState(false);
    const [notice, setNotice] = React.useState('');
    const openPlan = () => history.push(`${base}/plan`);

    const copilot = (headerAction: React.ReactNode) => <CopilotPanel headerAction={headerAction} onMockAction={setNotice} />;

    return (
        <ThemeProvider theme={verbaScholaTheme}>
            {/* As in the old builder mockup, the page scrolls below its header so the mockup banner never covers it. */}
            <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: PAGE_BACKGROUND }}>
                <Box component="header" sx={{ flexShrink: 0, minHeight: 72, px: { xs: 2, sm: 3, lg: 4 }, display: 'flex', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.96)', borderBottom: `1px solid ${BORDER}` }}>
                    <Button onClick={() => history.push('/mockup/teacher')} sx={{ p: 0, minWidth: 0, textTransform: 'none' }}>
                        <Box component="img" src={brandLockup} alt="VerbaSchola" sx={{ height: { xs: 22, sm: 25 }, width: 'auto', display: 'block' }} />
                    </Button>
                    <Box sx={{ ml: { xs: 1.5, md: 4 }, minWidth: 0 }}>
                        <BuilderStageTabs activeStep="description" onChange={step => history.push(step === 'description' ? `${base}/description-pdf` : `${base}/${step}`)} />
                    </Box>
                    <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 1 } }}>
                        <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => history.push(`${base}/overview`)}
                            sx={{ display: { xs: 'none', md: 'inline-flex' }, minHeight: 44, color: SLATE, fontFamily: INTER, fontWeight: 600, fontSize: 12.5, textTransform: 'none', whiteSpace: 'nowrap' }}>
                            Lesson overview
                        </Button>
                        <BuilderRefreshButton label="Refresh saved lesson" disabled={false} onClick={() => setNotice('Mockup: there is no saved lesson to refresh.')} />
                        <Avatar sx={{ width: 38, height: 38, ml: 0.75, backgroundColor: NAVY, fontFamily: POPPINS, fontSize: 13 }}>EF</Avatar>
                    </Box>
                </Box>

                <Box component="main" sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 2, sm: 3, lg: 4 }, py: 2 }}>
                    <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => history.push(`${base}/overview`)}
                        sx={{ display: { xs: 'inline-flex', md: 'none' }, minHeight: 44, color: SLATE, fontFamily: INTER, fontWeight: 600, fontSize: 12.5, textTransform: 'none', mb: 1, ml: -1 }}>
                        Lesson overview
                    </Button>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: `minmax(0, 1fr) ${copilotFolded ? 44 : COPILOT_WIDTH}px` }, gap: 3, alignItems: 'start' }}>
                        <Box sx={{ minWidth: 0 }}>
                            <LessonDescriptionPdfEditor onOpenPlan={openPlan} onMockAction={setNotice}
                                headerAction={compactCopilot && <DrawerIconButton side="right" label="Copilot" icon={<AutoAwesomeRoundedIcon fontSize="small" />} expanded={copilotOpen} onClick={() => setCopilotOpen(true)} />} />
                        </Box>
                        {!compactCopilot && (copilotFolded
                            ? <Tooltip title="Show Lesson copilot" placement="left">
                                <IconButton aria-label="Show Lesson copilot" onClick={() => setCopilotFolded(false)} sx={{ position: 'sticky', top: 0, width: 44, height: 44, border: `1px solid ${BORDER}`, borderRadius: '11px', backgroundColor: '#fff' }}>
                                    <AutoAwesomeRoundedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            : <Box sx={{ position: 'sticky', top: 0, minWidth: 0 }}>
                                {copilot(<Tooltip title="Hide Lesson copilot"><IconButton aria-label="Hide Lesson copilot" onClick={() => setCopilotFolded(true)} sx={{ my: -0.5, mr: -1 }}><KeyboardDoubleArrowRightRoundedIcon /></IconButton></Tooltip>)}
                            </Box>)}
                    </Box>
                </Box>
            </Box>

            {compactCopilot && <Drawer anchor="right" open={copilotOpen} onClose={() => setCopilotOpen(false)} PaperProps={{ sx: { width: 'min(92vw, 380px)' } }}>
                <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${BORDER}` }}>
                    <Typography sx={{ flex: 1, fontFamily: POPPINS, fontWeight: 600, color: NAVY }}>Lesson copilot</Typography>
                    <IconButton aria-label="Close lesson copilot" onClick={() => setCopilotOpen(false)} sx={{ width: 44, height: 44 }}><CloseRoundedIcon /></IconButton>
                </Stack>
                {copilot(null)}
            </Drawer>}

            <Snackbar open={Boolean(notice)} autoHideDuration={3500} onClose={() => setNotice('')} message={notice} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
        </ThemeProvider>
    );
}
